<script setup lang="ts" generic="TRow extends Record<string, unknown>">
import type { VNode } from 'vue';
import type { GroupedTableColumn, GroupedTableGroup } from '~~/types';

const props = withDefaults(defineProps<{
  columns: GroupedTableColumn[];
  groups: GroupedTableGroup<TRow>[];
  caption?: string;
  tableClass?: string;
  emptyText?: string;
  rowKey?: (row: TRow, index: number, group: GroupedTableGroup<TRow>) => string | number;
  rowClass?: (row: TRow, index: number, group: GroupedTableGroup<TRow>) => string | string[] | Record<string, boolean> | undefined;
}>(), {
  caption: undefined,
  tableClass: 'min-w-lg',
  emptyText: 'Keine Einträge vorhanden.',
  rowKey: undefined,
  rowClass: undefined,
});

defineSlots<{
  cell?(props: {
    row: TRow;
    rowIndex: number;
    column: GroupedTableColumn;
    group: GroupedTableGroup<TRow>;
  }): VNode[];
  'group-label'?(props: { group: GroupedTableGroup<TRow> }): VNode[];
}>();

function alignmentClass(alignment: GroupedTableColumn['align']): string {
  if (alignment === 'center') return 'text-center';
  if (alignment === 'right') return 'text-right';
  return 'text-left';
}

function resolveRowKey(row: TRow, index: number, group: GroupedTableGroup<TRow>): string | number {
  return props.rowKey?.(row, index, group) ?? `${String(group.key)}-${index}`;
}

const { containerRef, isDragging, onPointerDown, onPointerMove, onPointerUp, onClickCapture } = useDragScroll();

const tableRef = ref<HTMLTableElement | null>(null);
const stickyHeaderRef = ref<HTMLElement | null>(null);
const tableWidth = ref(0);
const headerColumnWidths = ref<number[]>([]);
let tableResizeObserver: ResizeObserver | undefined;
let observedTable: HTMLTableElement | null = null;

function observeCurrentTable() {
  if (tableRef.value === observedTable) return;
  if (observedTable) tableResizeObserver?.unobserve(observedTable);
  observedTable = tableRef.value;
  if (observedTable) tableResizeObserver?.observe(observedTable);
}

function syncHeaderMeasurements() {
  const table = tableRef.value;
  const cells = table?.tHead?.rows[0]?.cells;
  if (!table || !cells?.length) return;

  const nextTableWidth = Math.round(table.getBoundingClientRect().width * 100) / 100;
  const nextColumnWidths = Array.from(cells, cell => Math.round(cell.getBoundingClientRect().width * 100) / 100);
  const columnsChanged = nextColumnWidths.some((width, index) => width !== headerColumnWidths.value[index]);

  if (nextTableWidth !== tableWidth.value) tableWidth.value = nextTableWidth;
  if (columnsChanged || nextColumnWidths.length !== headerColumnWidths.value.length) {
    headerColumnWidths.value = nextColumnWidths;
  }
}

function onTableScroll() {
  if (stickyHeaderRef.value && containerRef.value) {
    stickyHeaderRef.value.scrollLeft = containerRef.value.scrollLeft;
  }
}

onMounted(() => {
  tableResizeObserver = new ResizeObserver(syncHeaderMeasurements);
  observeCurrentTable();
  syncHeaderMeasurements();
});

onUpdated(() => {
  observeCurrentTable();
  syncHeaderMeasurements();
});
onBeforeUnmount(() => tableResizeObserver?.disconnect());
</script>

<template>
  <div v-if="groups.length" class="rounded-lg border border-default">
    <!--
      Der horizontale Scroll-Container kann selbst kein viewport-sticky `<thead>`
      enthalten. Deshalb spiegelt diese semantisch ausgeblendete Kopie die exakt
      gemessenen Spaltenbreiten und bleibt am vertikalen Seiten-Scroll hängen.
    -->
    <div
      v-if="headerColumnWidths.length"
      ref="stickyHeaderRef"
      aria-hidden="true"
      class="pointer-events-none sticky top-0 z-30 -mb-10 h-10 overflow-hidden rounded-t-lg bg-elevated"
    >
      <table
        :class="['border-collapse text-sm', tableClass]"
        :style="{ width: `${tableWidth}px` }"
      >
        <colgroup>
          <col
            v-for="(width, index) in headerColumnWidths"
            :key="columns[index]?.key ?? index"
            :style="{ width: `${width}px` }"
          >
        </colgroup>
        <thead class="bg-elevated text-xs uppercase tracking-wide text-muted">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              scope="col"
              class="px-4 py-3 font-medium whitespace-nowrap"
              :class="[alignmentClass(column.align), column.headerClass]"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
      </table>
    </div>

    <div
      ref="containerRef"
      class="overflow-x-auto select-none"
      :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @click.capture="onClickCapture"
      @scroll.passive="onTableScroll"
    >
      <table ref="tableRef" :class="['w-full border-collapse text-sm', tableClass]">
        <caption v-if="caption" class="sr-only">{{ caption }}</caption>
        <thead
          class="h-10 bg-elevated text-xs uppercase tracking-wide text-muted"
          :class="headerColumnWidths.length ? 'opacity-0' : undefined"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              scope="col"
              class="px-4 py-3 font-medium whitespace-nowrap"
              :class="[alignmentClass(column.align), column.headerClass]"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody v-for="group in groups" :key="group.key">
          <tr class="border-y border-default bg-elevated/40">
            <th
              :colspan="columns.length"
              scope="rowgroup"
              class="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-highlighted"
            >
              <slot name="group-label" :group="group">{{ group.label }}</slot>
            </th>
          </tr>
          <tr
            v-for="(row, rowIndex) in group.rows"
            :key="resolveRowKey(row, rowIndex, group)"
            class="border-b border-default/60 last:border-b-0"
            :class="rowClass?.(row, rowIndex, group)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-4 py-3 text-highlighted"
              :class="[alignmentClass(column.align), column.cellClass]"
            >
              <slot
                name="cell"
                :row="row"
                :row-index="rowIndex"
                :column="column"
                :group="group"
              >
                {{ row[column.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <p v-else class="rounded-lg border border-dashed border-default px-4 py-8 text-center text-sm text-muted">
    {{ emptyText }}
  </p>
</template>
